import { useState, useMemo } from 'react';
import { Modal, FormSelect, FormNumberInput, Button } from '@components/UI';
import { parseNumericDraft, toNumericDraft } from '@components/UI/numericDraft';
import { nanoid } from 'nanoid';
import { getTradeFilters } from '@/utils/tradeFilters';
import type { TradeFilter, TradeDirection, EntityTypeUnion } from '@einhaus/project314-types';

interface TradeFilterModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (filter: TradeFilter) => void;
}

const TradeFilterModal = ({ isOpen, onClose, onSave }: TradeFilterModalProps) => {
    const [entityType, setEntityType] = useState<string>('');
    const [tradeDirection, setTradeDirection] = useState<string>('');
    const [filterField, setFilterField] = useState<string>('');
    const [operator, setOperator] = useState<string>('');
    const [value, setValue] = useState<string>('');
    const tradeFilters = useMemo(() => getTradeFilters(), []);

    const handleSave = () => {
        if (!entityType || !filterField || !operator || !value) {
            return;
        }

        const filter: TradeFilter = {
            id: nanoid(10),
            entityType: entityType === 'AssetOption' ? 'Asset' : (entityType as EntityTypeUnion['entityType']),
            tradeDirection: tradeDirection ? (tradeDirection as TradeDirection | 'either') : 'either',
            field: filterField,
            operator: operator as 'greaterThanEqual' | 'lessThanEqual' | 'equal' | 'greaterThan' | 'lessThan',
            value: +value
        };

        onSave(filter);
        resetForm();
        onClose();
    };

    const resetForm = () => {
        setEntityType('');
        setTradeDirection('');
        setFilterField('');
        setOperator('');
        setValue('');
    };

    const entityTypeOptions = [
        { value: '', label: 'Choose:', disabled: true },
        { value: 'Asset', label: 'Asset' },
        { value: 'Cryptocurrency', label: 'Cryptocurrency' },
        { value: 'AssetIndex', label: 'Asset Index' }
    ];

    const tradeDirectionOptions = [
        { value: '', label: 'Either' },
        { value: 'long', label: 'Long' },
        { value: 'short', label: 'Short' }
    ];

    const filterOptions = [
        { value: '', label: 'Choose:', disabled: true },
        ...tradeFilters.map((filter) => ({ value: filter, label: filter }))
    ];

    const operatorOptions = [
        { value: '', label: 'Choose:', disabled: true },
        { value: 'greaterThan', label: '>' },
        { value: 'lessThan', label: '<' },
        { value: 'greaterThanEqual', label: '>=' },
        { value: 'lessThanEqual', label: '<=' },
        { value: 'equal', label: '=' }
    ];

    const modalFooter = (
        <>
            <Button variant="secondary" onClick={onClose}>
                Cancel
            </Button>
            <Button disabled={!entityType || !filterField || !operator || !value} variant="primary" onClick={handleSave}>
                Save
            </Button>
        </>
    );

    return (
        <Modal footer={modalFooter} isOpen={isOpen} title="Add Trade Filter" onClose={onClose}>
            <div className="mb-3">
                <FormSelect
                    id="entityTypeSelect"
                    label="Entity Type"
                    options={entityTypeOptions}
                    value={entityType}
                    onChange={(e) => setEntityType(e.target.value)}
                />
            </div>
            <div className="mb-3">
                <FormSelect
                    id="tradeDirectionSelect"
                    label="Position side"
                    options={tradeDirectionOptions}
                    value={tradeDirection}
                    onChange={(e) => setTradeDirection(e.target.value)}
                />
            </div>
            <div className="mb-3">
                <FormSelect
                    id="filterSelect"
                    label="Filter"
                    options={filterOptions}
                    value={filterField}
                    onChange={(e) => setFilterField(e.target.value)}
                />
            </div>
            <div className="mb-3">
                <FormSelect
                    id="operatorSelect"
                    label="Operator"
                    options={operatorOptions}
                    value={operator}
                    onChange={(e) => setOperator(e.target.value)}
                />
            </div>
            <div className="mb-3">
                <FormNumberInput
                    id="value"
                    label="Value"
                    placeholder="Value"
                    value={parseNumericDraft(value)}
                    onValueChange={(parsed) => setValue(toNumericDraft(parsed))}
                />
            </div>
        </Modal>
    );
};

export default TradeFilterModal;
